import React, { FC, useMemo } from 'react';
import { ChatBox } from './components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Store } from '../../../redux';
import { ErrorLayout } from '../../../shared/components';
import { limit, or, orderBy, where } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { firebaseAuth } from '../../../shared/lib';
import { Theme, useMediaQuery } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { DMPage } from './dm';
import { CHAT_PAGE_ROUTE, DM_PAGE_ROUTE } from './route.definitions';
import '../components/social.css';

interface ChatPageProps {
  avoidErrorMessage?: boolean;
  /**
   * When true, ChatPage is rendered inside an already-constrained
   * flex container (e.g. the desktop split-pane section). The component
   * skips its own viewport-height wrapper so it fills the parent instead.
   */
  embedded?: boolean;
}

const NATIVE_CHAT_HEIGHT =
  'calc(100dvh - 60px - env(safe-area-inset-top) - var(--kb-safe-bottom, env(safe-area-inset-bottom)) - var(--kb-inset, 0px))';

export const chatSurfaceClassName = (base: string) =>
  `${base}${Capacitor.isNativePlatform() ? ' bh-chat-native' : ' bh-chat-web'}`;

export const chatSurfaceStyle = () =>
  Capacitor.isNativePlatform()
    ? {
        height: NATIVE_CHAT_HEIGHT,
        transition: 'height var(--kb-transition, 0ms linear)',
      }
    : undefined;

export const ChatPage: FC<ChatPageProps> = ({
  avoidErrorMessage = false,
  embedded = false,
}) => {
  const [params] = useSearchParams();
  const receiverID = params.get('receiverID');
  const { room } = useSelector((state: Store) => state.inPlayReducer.gameInfo);

  const [user] = useAuthState(firebaseAuth);

  const queryConstraint = useMemo(() => {
    if (!receiverID?.length && !room?.roomID) {
      return [];
    }

    if (!user) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const qc: any[] = [orderBy('createdAt', 'desc'), limit(50)];
    if (receiverID) {
      qc.push(
        or(
          where('threadID', '==', [receiverID, user.uid]),
          where('threadID', '==', [user.uid, receiverID])
        )
      );
      return qc;
    }
    if (room?.roomID) {
      qc.push(where('groupID', '==', room.roomID));

      return qc;
    }

    return [];
  }, [receiverID, room?.roomID, user]);

  if (!receiverID?.length && !room?.roomID && !avoidErrorMessage) {
    return (
      <ErrorLayout
        outerBoxProps={{ height: 'auto' }}
        texts={{
          defaultError: `Missing user info to start a chat`,
        }}
      />
    );
  }

  const chatBox = (
    <ChatBox
      outerBoxProps={{ className: 'flex flex-col flex-1 min-h-0' }}
      sendMessageProps={{
        receiverID: receiverID || '',
        roomID: room?.roomID || '',
      }}
      queryConstraint={queryConstraint}
    />
  );

  if (embedded) {
    return chatBox;
  }

  return (
    <div
      className={chatSurfaceClassName(
        'bh-chat-page bh-chat-page--conversation'
      )}
      style={chatSurfaceStyle()}
    >
      {chatBox}
    </div>
  );
};

/**
 * Desktop / tablet split-pane layout:
 * - Left column: DM threads list
 * - Right column: active chat (or empty-state hint)
 *
 * On mobile a separate single-pane flow is used (see BhabiDMPage).
 */
export const LgScreenChatPage: FC = () => {
  const [search] = useSearchParams();
  const receiverID = search.get('receiverID');
  const missingUID = !receiverID;
  const nav = useNavigate();

  const onClickThread = (id?: string) => () => {
    if (!id) {
      return;
    }

    const sp = new URLSearchParams({ receiverID: id }).toString();
    nav(DM_PAGE_ROUTE.ABSOLUTE_PATH + `?${sp}`);
  };

  return (
    <div
      className={chatSurfaceClassName('bh-dm-layout')}
      style={chatSurfaceStyle()}
    >
      {/* Left pane: thread list */}
      <aside
        className="bh-chat-pane min-h-0 overflow-hidden"
        aria-label="Conversations"
      >
        <DMPage
          onClickThread={onClickThread}
          activeThreadID={receiverID}
          textFieldProps={{ sx: { width: '100%' } }}
        />
      </aside>
      {/* Right pane: active chat */}
      <section
        className="bh-chat-pane min-h-0 overflow-hidden flex flex-col"
        aria-label="Conversation"
      >
        {missingUID ? (
          <div className="bh-chat__empty">
            <div className="bh-chat__empty-inner">
              <div className="bh-chat__empty-icon" aria-hidden="true">
                ♦
              </div>
              <h2 className="bh-h3">Pick a conversation</h2>
              <p className="bh-muted bh-small">
                Choose someone from the list, or search to start a new chat.
              </p>
            </div>
          </div>
        ) : (
          <ChatPage avoidErrorMessage embedded />
        )}
      </section>
    </div>
  );
};

export const BhabiDMPage: FC = () => {
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const nav = useNavigate();

  // On mobile we navigate to the full-screen single-pane chat. On
  // desktop / tablet the split-pane is already wired to render the active
  // chat in the right column based on the receiverID search param —
  // navigating to /dm/chat would unmount the sidebar, which is exactly
  // what we don't want on bigger screens.
  const onClickThread = (id?: string) => () => {
    if (!id) return;
    const sp = new URLSearchParams({ receiverID: id }).toString();
    nav(
      (isMobile ? CHAT_PAGE_ROUTE.ABSOLUTE_PATH : DM_PAGE_ROUTE.ABSOLUTE_PATH) +
        `?${sp}`
    );
  };

  return !isMobile ? (
    <LgScreenChatPage />
  ) : (
    <div
      className={chatSurfaceClassName('bh-chat-page bh-chat-page--threads')}
      style={chatSurfaceStyle()}
    >
      <DMPage onClickThread={onClickThread} />
    </div>
  );
};
